// @flow

import * as React from "react";
import {
  isAfter,
  isBefore,
  getDate,
  getMonth,
  getYear,
  startOfMonth,
  getDaysInMonth,
  getDay,
  format,
  isEqual,
  parse
} from "date-fns";

import {
  datePickerDisplayFormat as datePickerDisplayFormatDefault,
  datePickerFormFormat as datePickerFormFormatDefault,
  monthPickerDisplayFormat as monthPickerDisplayFormatDefault,
  monthPickerFormFormat as monthPickerFormFormatDefault
} from "../../../utils/formats";
import type { InputType } from "../../Input/TextInput";

export type MonthsPropsType = Array<{
  name: string,
  onClick: (closePopover: () => void) => void,
  isSelectable: boolean,
  isSelected: boolean
}>;

export type DaysPropsType = Array<null | {
  day: number,
  onClick: (closePopover: () => void) => void,
  isSelectable: boolean,
  isSelected: boolean,
  isLastDay: boolean
}>;

type ChildrenType = {
  yearNavProps: {
    label: string,
    setPreviousYear: () => void,
    setNextYear: () => void,
    isNextYearSelectable: boolean,
    isPreviousYearSelectable: boolean,
    ariaLabelPrevious: string,
    ariaLabelNext: string
  },
  monthPickerProps: {
    monthsProps: MonthsPropsType,
    monthPickerValue: string,
    onOpenMonthPicker: () => void
  },
  monthNavProps: {
    label: string,
    setPreviousMonth: () => void,
    setNextMonth: () => void,
    isPreviousMonthSelectable: boolean,
    isNextMonthSelectable: boolean,
    ariaLabelPrevious: string,
    ariaLabelNext: string
  },
  datePickerProps: {
    daysProps: DaysPropsType,
    weekDays: Array<string>,
    datePickerValue: string,
    onOpenDatePicker: () => void
  },
  shared: {
    onKeyDown: (e: KeyboardEvent) => void
  }
};

type DatePickersLogicProps = {
  children: (childrenArgs: ChildrenType) => React.Node,
  input: InputType,
  startDate: Date,
  minDate: Date,
  maxDate: Date,
  shouldDisallowDate: Date => boolean,
  datePickerDisplayFormat: string,
  datePickerFormFormat: string,
  monthPickerDisplayFormat: string,
  monthPickerFormFormat: string
};

type DatePickersLogicState = {
  year: number,
  month: number
};

const months = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember"
];

const weekDays = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

class DatePickersLogic extends React.Component<DatePickersLogicProps, DatePickersLogicState> {
  static defaultProps = {
    datePickerDisplayFormat: datePickerDisplayFormatDefault,
    datePickerFormFormat: datePickerFormFormatDefault,
    monthPickerDisplayFormat: monthPickerDisplayFormatDefault,
    monthPickerFormFormat: monthPickerFormFormatDefault,
    shouldDisallowDate: () => false
  };

  state = {
    year: 0,
    month: 0
  };

  isDateSelectable = (pickerType: "year" | "month" | "date", current: Date) => {
    const { minDate, maxDate, shouldDisallowDate } = this.props;

    const getDateToCompareForPicker = this.getDateToCompare(pickerType);
    const parsedCurrent = getDateToCompareForPicker(current);
    const parsedMinDate = getDateToCompareForPicker(minDate);
    const parsedMaxDate = getDateToCompareForPicker(maxDate);

    const isWithinBounds =
      (isEqual(parsedCurrent, parsedMinDate) || isAfter(parsedCurrent, parsedMinDate)) &&
      (isEqual(parsedCurrent, parsedMaxDate) || isBefore(parsedCurrent, parsedMaxDate));

    if (pickerType === "date" && shouldDisallowDate) {
      return isWithinBounds && !shouldDisallowDate(current);
    }
    return isWithinBounds;
  };

  getDateToCompare = (pickerType: "year" | "month" | "date") => (date: Date) =>
    new Date(
      getYear(date),
      pickerType === "month" || pickerType === "date" ? getMonth(date) : 0,
      pickerType === "date" ? getDate(date) : 1
    );

  getDateFromValue = (
    pickerType: "month" | "date",
    defaultYear: number = 0,
    defaultMonth: number = 0,
    defaultDay: number = 1
  ) => {
    const {
      input: { value },
      datePickerFormFormat,
      monthPickerFormFormat
    } = this.props;

    if (value) {
      const formatConfig = {
        date: datePickerFormFormat,
        month: monthPickerFormFormat
      };

      return parse(
        value,
        formatConfig[pickerType],
        new Date(defaultYear, defaultMonth, defaultDay)
      );
    }

    return new Date(defaultYear, defaultMonth, defaultDay);
  };

  getDayMonthYearFromStartDate = () => {
    const { startDate } = this.props;

    return {
      startYear: getYear(startDate),
      startMonth: getMonth(startDate),
      startDay: getDate(startDate)
    };
  };

  /**
   * Year picker
   */

  setPreviousYear = () =>
    this.setState(state => ({
      year: state.year - 1
    }));

  setNextYear = () =>
    this.setState(state => ({
      year: state.year + 1
    }));

  setMonth = (month: number) => this.setState({ month });

  isNextYearSelectable = (): boolean => {
    const { year } = this.state;
    const { minDate } = this.props;

    const minYear = getYear(minDate);

    return year < minYear || this.isDateSelectable("year", new Date(year + 1, 0, 1));
  };

  isPreviousYearSelectable = (): boolean => {
    const { year } = this.state;
    const { maxDate } = this.props;

    const maxYear = getYear(maxDate);

    return year > maxYear || this.isDateSelectable("year", new Date(year - 1, 11, 31));
  };

  /**
   * Month picker
   */

  getMonthProps = (): (Array<{
    name: string,
    onClick: (closePopover: () => void) => void,
    isSelectable: boolean,
    isSelected: boolean
  }>) => {
    const { year } = this.state;
    const date = this.getDateFromValue("month");

    return months.map((month, index) => ({
      name: month,
      onClick: (closePopover: () => void) => this.chooseMonth(index, closePopover),
      isSelectable: this.isDateSelectable("month", new Date(year, index, 1)),
      isSelected: isEqual(date, new Date(year, index))
    }));
  };

  chooseMonth = (index: number, closePopover: () => void) => {
    const { year } = this.state;
    const {
      input: { onChange },
      monthPickerFormFormat
    } = this.props;

    const chosenDate = format(new Date(year, index), monthPickerFormFormat);
    onChange(chosenDate);

    closePopover();
  };

  getMonthPickerValue = () => {
    const {
      input: { value },
      monthPickerDisplayFormat
    } = this.props;

    if (!value) {
      return "";
    }

    const date = this.getDateFromValue("month");
    return format(date, monthPickerDisplayFormat);
  };

  onOpenMonthPicker = () => {
    const { startYear, startMonth } = this.getDayMonthYearFromStartDate();

    const date = this.getDateFromValue("month", startYear, startMonth);

    this.setState({
      year: getYear(date)
    });
  };

  /**
   * Date picker
   */

  calculateNextMonth = () => {
    const { month, year } = this.state;

    const nextMonth = month + 1;
    const isNextYear = nextMonth === 12;

    return {
      month: isNextYear ? 0 : nextMonth,
      year: isNextYear ? year + 1 : year
    };
  };

  calculatePreviousMonth = () => {
    const { month, year } = this.state;

    const previousMonth = month - 1;
    const isPreviousYear = previousMonth === -1;

    return {
      month: isPreviousYear ? 11 : previousMonth,
      year: isPreviousYear ? year - 1 : year
    };
  };

  setPreviousMonth = () => this.setState(this.calculatePreviousMonth());

  setNextMonth = () => this.setState(this.calculateNextMonth());

  isPreviousMonthSelectable = (): boolean => {
    const { month, year } = this.calculatePreviousMonth();
    const { maxDate } = this.props;
    const numberOfDaysInMonth = getDaysInMonth(new Date(year, month));
    const currentDate = new Date(year, month, numberOfDaysInMonth);

    return (
      this.isDateSelectable("month", new Date(year, month, numberOfDaysInMonth)) ||
      isAfter(currentDate, maxDate)
    );
  };

  isNextMonthSelectable = (): boolean => {
    const { month, year } = this.calculateNextMonth();
    const { minDate } = this.props;
    const currentDate = new Date(year, month, 1);

    return this.isDateSelectable("month", currentDate) || isBefore(currentDate, minDate);
  };

  getDaysProps = () => {
    const { month, year } = this.state;

    const date = new Date(year, month);
    const indexOfDay = getDay(startOfMonth(date)) || 7;
    const daysInMonth = getDaysInMonth(date);

    const arrayOfDayIndexes = Array.from(Array(daysInMonth), (x, index) => index + 1);
    const yearMonthAndDay = this.getDateFromValue("date");

    const listOfDays = arrayOfDayIndexes.map(day => ({
      day,
      onClick: (closePopover: () => void) => this.chooseDay(day, closePopover),
      isSelectable: this.isDateSelectable("date", new Date(year, month, day)),
      isSelected: isEqual(yearMonthAndDay, new Date(year, month, day)),
      isLastDay: arrayOfDayIndexes.length === day
    }));

    const emptyDaysFromTheStart = Array(indexOfDay - 1).fill(null);

    const numberOfDaysWithEmpty = emptyDaysFromTheStart.length + listOfDays.length;
    const emptyDaysFromTheEnd = Array(7 - (numberOfDaysWithEmpty % 7)).fill(null);
    const listOfDaysWithEmptyDays = [
      ...emptyDaysFromTheStart,
      ...listOfDays,
      ...emptyDaysFromTheEnd
    ];

    return listOfDaysWithEmptyDays;
  };

  chooseDay = (index: number, closePopover: () => void) => {
    const { year, month } = this.state;
    const {
      input: { onChange },
      datePickerFormFormat
    } = this.props;

    const chosenMonthAndYear = format(new Date(year, month, index), datePickerFormFormat);
    onChange(chosenMonthAndYear);

    closePopover();
  };

  getDatePickerValue = () => {
    const {
      input: { value },
      datePickerDisplayFormat
    } = this.props;

    if (!value) {
      return "";
    }

    const date = this.getDateFromValue("date");
    return format(date, datePickerDisplayFormat);
  };

  onOpenDatePicker = () => {
    const { startYear, startMonth, startDay } = this.getDayMonthYearFromStartDate();

    const date = this.getDateFromValue("date", startYear, startMonth, startDay);

    this.setState({
      year: getYear(date),
      month: getMonth(date)
    });
  };

  /*
   * Shared
   */

  handleKeyDown = (e: KeyboardEvent) => {
    const { input } = this.props;

    if (e.key === "Backspace") {
      input.onChange();
    }
  };

  getProps = () => {
    const { year, month } = this.state;

    return {
      yearNavProps: {
        label: year.toString(),
        setPreviousYear: this.setPreviousYear,
        setNextYear: this.setNextYear,
        isNextYearSelectable: this.isNextYearSelectable(),
        isPreviousYearSelectable: this.isPreviousYearSelectable(),
        ariaLabelPrevious: `Vorjahr`,
        ariaLabelNext: `Nächstes`
      },
      monthPickerProps: {
        monthsProps: this.getMonthProps(),
        monthPickerValue: this.getMonthPickerValue(),
        onOpenMonthPicker: this.onOpenMonthPicker
      },
      monthNavProps: {
        label: `${months[month]} ${year}`,
        setPreviousMonth: this.setPreviousMonth,
        setNextMonth: this.setNextMonth,
        isPreviousMonthSelectable: this.isPreviousMonthSelectable(),
        isNextMonthSelectable: this.isNextMonthSelectable(),
        ariaLabelPrevious: `Vormonat`,
        ariaLabelNext: `Nächsten Monat`
      },
      datePickerProps: {
        daysProps: this.getDaysProps(),
        weekDays,
        datePickerValue: this.getDatePickerValue(),
        onOpenDatePicker: this.onOpenDatePicker
      },
      shared: {
        onKeyDown: this.handleKeyDown
      }
    };
  };

  render() {
    const { children } = this.props;
    const props = this.getProps();

    return children(props);
  }
}

export default DatePickersLogic;
